import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  useBodyAreasQuery,
  useDevicesQuery,
  useTreatmentCategoriesQuery,
  useTreatmentsQuery,
} from '@/hooks/useTreatmentLibraryQueries';
import { TreatmentsPane } from './TreatmentsPane';
import { DevicesPane } from './DevicesPane';
import { CategoriesEditor } from './CategoriesEditor';
import { BodyAreasEditor } from './BodyAreasEditor';
import { TREATMENT_LIBRARY_VIEW_SEARCH_PARAMETER } from '@/components/assistant/uiActions/assistantDestinations';

type TreatmentLibraryView = 'treatments' | 'devices' | 'categories' | 'areas';

const TREATMENT_LIBRARY_VIEWS: TreatmentLibraryView[] = [
  'treatments',
  'devices',
  'categories',
  'areas',
];

function parseTreatmentLibraryView(value: string | null): TreatmentLibraryView {
  const match = TREATMENT_LIBRARY_VIEWS.find(function isRequested(view) {
    return view === value;
  });
  return match === undefined ? 'treatments' : match;
}

export function TreatmentLibrarySection() {
  const { t } = useTranslation('treatmentLibrary');
  const [searchParameters, setSearchParameters] = useSearchParams();
  const view = parseTreatmentLibraryView(
    searchParameters.get(TREATMENT_LIBRARY_VIEW_SEARCH_PARAMETER)
  );
  function setView(nextView: TreatmentLibraryView) {
    const nextParameters = new URLSearchParams(searchParameters);
    nextParameters.set(TREATMENT_LIBRARY_VIEW_SEARCH_PARAMETER, nextView);
    setSearchParameters(nextParameters, { replace: true });
  }

  const treatmentsQuery = useTreatmentsQuery('', null, true, true);
  const devicesQuery = useDevicesQuery(true);
  const categoriesQuery = useTreatmentCategoriesQuery(true);
  const bodyAreasQuery = useBodyAreasQuery(true);

  const tabs: { id: TreatmentLibraryView; label: string; count: number | null }[] = [
    {
      id: 'treatments',
      label: t('tabs.treatments'),
      count: treatmentsQuery.data === undefined ? null : treatmentsQuery.data.length,
    },
    {
      id: 'devices',
      label: t('tabs.devices'),
      count: devicesQuery.data === undefined ? null : devicesQuery.data.length,
    },
    {
      id: 'categories',
      label: t('tabs.categories'),
      count: categoriesQuery.data === undefined ? null : categoriesQuery.data.length,
    },
    {
      id: 'areas',
      label: t('tabs.bodyAreas'),
      count: bodyAreasQuery.data === undefined ? null : bodyAreasQuery.data.length,
    },
  ];

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-zooindigo">{t('title')}</h2>
        <p className="text-sm text-zoomuted mt-0.5">{t('subtitle')}</p>
      </div>

      <div className="inline-flex flex-wrap bg-zoovpalelavender rounded-xl p-1 gap-1 mb-5">
        {tabs.map(function renderTab(tab) {
          const active = view === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={function select() {
                setView(tab.id);
              }}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold transition-all',
                active
                  ? 'bg-zoowhite text-zooindigo shadow-sm'
                  : 'text-zoomuted hover:text-zooindigo'
              )}
            >
              {tab.label}
              {tab.count === null ? null : (
                <span
                  className={cn(
                    'text-[11px] font-bold rounded-full px-2 py-0.5',
                    active ? 'bg-zoovpalelavender text-zooprimary' : 'bg-zoowhite/60 text-zoomuted'
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {view === 'treatments' ? (
        <TreatmentsPane
          onManageBodyAreas={function goToAreas() {
            setView('areas');
          }}
          onManageDevices={function goToDevices() {
            setView('devices');
          }}
        />
      ) : null}
      {view === 'devices' ? <DevicesPane /> : null}
      {view === 'categories' ? <CategoriesEditor /> : null}
      {view === 'areas' ? <BodyAreasEditor /> : null}
    </div>
  );
}
